import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, BellIcon, UserIcon, RefreshCwIcon, LogOutIcon, AlertTriangleIcon, ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface HeaderProps {
  onMenuClick: () => void;
}
export function Header({
  onMenuClick
}: HeaderProps) {
  const {
    role,
    userName,
    isDemoUser,
    setRole,
    logout
  } = useAuth();
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const roleSwitcherRef = useRef<HTMLDivElement>(null);
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (roleSwitcherRef.current && !roleSwitcherRef.current.contains(event.target as Node)) {
        setShowRoleSwitcher(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Mock notifications
  const notifications = [{
    id: 1,
    type: 'schedule_change',
    message: 'Training op 12 juni verplaatst naar Rotterdam',
    time: '2 uur geleden',
    read: false
  }, {
    id: 2,
    type: 'warning',
    message: 'Ziekmelding instructeur Jan Smit',
    time: '3 uur geleden',
    read: false
  }, {
    id: 3,
    type: 'info',
    message: 'Nieuwe training ingepland voor 15 juni',
    time: '5 uur geleden',
    read: true
  }];
  const unreadCount = notifications.filter(n => !n.read).length;
  const getRoleColor = () => {
    switch (role) {
      case 'instructor':
        return 'bg-green-100 text-green-800';
      case 'planner':
        return 'bg-blue-100 text-blue-800';
      case 'manager':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getRoleIcon = () => {
    switch (role) {
      case 'instructor':
        return '👤';
      case 'planner':
        return '🧭';
      case 'manager':
        return '📊';
      default:
        return '👤';
    }
  };
  const handleRoleSwitch = (newRole: 'instructor' | 'planner' | 'manager') => {
    setRole(newRole);
    setShowRoleSwitcher(false);
  };
  return <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-20" data-id="element-1766">
      <div className="flex justify-between items-center px-4 py-3" data-id="element-1767">
        <div className="flex items-center gap-4" data-id="element-1768">
          <button onClick={onMenuClick} className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none transition-colors duration-200 md:hidden" aria-label="Toggle menu" data-id="element-1769">
            <MenuIcon size={20} data-id="element-1770" />
          </button>
          <div className="hidden md:block" data-id="element-1771">
            <button onClick={() => setShowSearch(!showSearch)} className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none transition-colors duration-200" aria-label="Search" data-id="element-1772">
              <SearchIcon size={20} data-id="element-1773" />
            </button>
          </div>
          {showSearch && <div className="absolute top-full left-0 right-0 bg-white p-2 shadow-md border-t border-gray-200 md:top-0 md:left-auto md:right-auto md:relative md:border-0 md:shadow-none md:bg-transparent" data-id="element-1774">
              <div className="relative" data-id="element-1775">
                <input type="text" placeholder="Zoeken..." className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C] focus:border-transparent transition-all duration-200" autoFocus data-id="element-1776" />
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-1777" />
                <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setShowSearch(false)} data-id="element-1778">
                  <XIcon size={16} data-id="element-1779" />
                </button>
              </div>
            </div>}
        </div>
        {isDemoUser && <div className="hidden md:flex items-center bg-yellow-50 text-yellow-800 px-3 py-1 rounded-full text-xs font-medium border border-yellow-200" data-id="element-1780">
            <AlertTriangleIcon size={14} className="mr-1" data-id="element-1781" />
            Demo Modus Actief
          </div>}
        <div className="flex items-center space-x-3" data-id="element-1782">
          {/* Notifications */}
          <div className="relative" ref={notificationRef} data-id="element-1783">
            <button className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none transition-colors duration-200" onClick={() => {
            setShowNotifications(!showNotifications);
            setShowUserMenu(false);
            setShowRoleSwitcher(false);
          }} aria-label="Notifications" data-id="element-1784">
              <BellIcon size={20} data-id="element-1785" />
              {unreadCount > 0 && <span className="absolute top-1 right-1 w-4 h-4 bg-[#B61C1C] rounded-full text-white text-xs flex items-center justify-center" data-id="element-1786">
                  {unreadCount}
                </span>}
            </button>
            {showNotifications && <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-30 border border-gray-200 animate-fadeIn" data-id="element-1787">
                <div className="p-3 border-b border-gray-200 flex justify-between items-center" data-id="element-1788">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-1789">
                    Meldingen
                  </h3>
                  <Badge variant="danger" size="sm" data-id="element-1790">
                    {unreadCount} Nieuw
                  </Badge>
                </div>
                <div className="max-h-80 overflow-y-auto" data-id="element-1791">
                  {notifications.length > 0 ? <div className="py-1" data-id="element-1792">
                      {notifications.map(notification => <div key={notification.id} className={`px-4 py-3 hover:bg-gray-50 transition-colors duration-150 ${!notification.read ? 'bg-blue-50' : ''}`} data-id="element-1793">
                          <div className="flex justify-between items-start" data-id="element-1794">
                            <p className="text-sm font-medium text-gray-900" data-id="element-1795">
                              {notification.message}
                            </p>
                            {!notification.read && <span className="w-2 h-2 bg-blue-600 rounded-full" data-id="element-1796"></span>}
                          </div>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-1797">
                            {notification.time}
                          </p>
                        </div>)}
                    </div> : <div className="px-4 py-6 text-center text-gray-500 text-sm" data-id="element-1798">
                      Geen nieuwe meldingen
                    </div>}
                </div>
                <div className="p-2 border-t border-gray-200 text-center" data-id="element-1799">
                  <button className="text-xs text-[#B61C1C] hover:text-[#961818] font-medium transition-colors duration-200" data-id="element-1800">
                    Alle meldingen markeren als gelezen
                  </button>
                </div>
              </div>}
          </div>
          {/* Role Switcher (Demo Mode) */}
          {isDemoUser && <div className="relative" ref={roleSwitcherRef} data-id="element-1801">
              <button onClick={() => {
            setShowRoleSwitcher(!showRoleSwitcher);
            setShowNotifications(false);
            setShowUserMenu(false);
          }} className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none transition-colors duration-200 flex items-center" title="Wissel rol" data-id="element-1802">
                <RefreshCwIcon size={18} data-id="element-1803" />
                <span className="ml-1 text-xs hidden sm:inline" data-id="element-1804">
                  Wissel rol
                </span>
              </button>
              {showRoleSwitcher && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-30 border border-gray-200 animate-fadeIn" data-id="element-1805">
                  <div className="py-1" data-id="element-1806">
                    <button onClick={() => handleRoleSwitch('instructor')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150" data-id="element-1807">
                      👤 Instructeur
                    </button>
                    <button onClick={() => handleRoleSwitch('planner')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150" data-id="element-1808">
                      🧭 Planner
                    </button>
                    <button onClick={() => handleRoleSwitch('manager')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150" data-id="element-1809">
                      📊 Manager
                    </button>
                  </div>
                </div>}
            </div>}
          {/* User Profile */}
          <div className="relative" ref={userMenuRef} data-id="element-1810">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => {
            setShowUserMenu(!showUserMenu);
            setShowNotifications(false);
            setShowRoleSwitcher(false);
          }} data-id="element-1811">
              <div className="hidden md:block text-right" data-id="element-1812">
                <p className="text-sm font-medium text-gray-900" data-id="element-1813">{userName}</p>
                <div className="flex items-center" data-id="element-1814">
                  <p className={`text-xs px-1.5 py-0.5 rounded-full capitalize ${getRoleColor()}`} data-id="element-1815">
                    {getRoleIcon()} {role}
                  </p>
                  {isDemoUser && <span className="ml-1 text-xs text-gray-500" data-id="element-1816">(Demo)</span>}
                </div>
              </div>
              <div className="flex items-center" data-id="element-1817">
                <div className="w-8 h-8 rounded-full bg-[#B61C1C] flex items-center justify-center text-white" data-id="element-1818">
                  <UserIcon size={16} data-id="element-1819" />
                </div>
                <ChevronDownIcon size={16} className="text-gray-500 ml-1 hidden md:block" data-id="element-1820" />
              </div>
            </div>
            {showUserMenu && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-30 border border-gray-200 animate-fadeIn" data-id="element-1821">
                <div className="py-1" data-id="element-1822">
                  <a href="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150" data-id="element-1823">
                    Profiel
                  </a>
                  <a href="/settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150" data-id="element-1824">
                    Instellingen
                  </a>
                  <div className="border-t border-gray-200 my-1" data-id="element-1825"></div>
                  <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150" data-id="element-1826">
                    <div className="flex items-center" data-id="element-1827">
                      <LogOutIcon size={14} className="mr-2" data-id="element-1828" />
                      Uitloggen
                    </div>
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
      {/* Role indicator banner */}
      {isDemoUser && <div className={`text-xs px-4 py-1.5 text-center ${role === 'instructor' ? 'bg-green-50 text-green-800' : role === 'planner' ? 'bg-blue-50 text-blue-800' : 'bg-purple-50 text-purple-800'}`} data-id="element-1829">
          Ingelogd als:{' '}
          <span className="font-medium" data-id="element-1830">
            {role === 'instructor' ? 'Instructeur' : role === 'planner' ? 'Planner' : 'Manager'}
          </span>{' '}
          (Demo)
        </div>}
    </header>;
}